<template>
  <div class="content-container">
    <div v-if="isShownDescription || isShownContentUrl" class="content-section description">
      <div
        class="section-title"
        v-text="$t('bill.description')"
      ></div>

      <div class="section-content">
        <template v-if="transaction.description">
          <p
            v-for="description in splitedDescription"
            :key="description"
            class="section-description"
            v-text="description"
          ></p>
        </template>

        <template v-if="isShownContentUrl">
          <p class="section-url">
            <a
              @click="$openLink(getContentUrlByCasualType, true, true)"
              v-text="$t('realised.content')"
            ></a>
          </p>
        </template>
      </div>
    </div>

    <div v-if="isTips" class="content-section tips">
      <div
        class="section-title"
        v-text="$t('bill.tips.section.title')"
      ></div>
      <div class="section-content">
        <div :class="$style.tipsSection">
          <div
            v-for="(tip, idx) in tipsVariants"
            :key="idx"
            :class="$style.tipsVariant"
            v-text="tip"
          ></div>
          <div
            v-if="permission"
            v-cursor:pointer
            :class="$style.tipsVariant"
            @click="handleChangeTipsVariants"
          >
            <base-icon name="basic_pen-create" :size="14" />
          </div>
        </div>
      </div>
    </div>

    <template v-if="isSubscription || isTariff || isTicket">
      <bill-tariff
        v-for="tariff in billTariffs"
        :key="tariff.id"
        :transaction="transaction"
        :tariff="tariff"
        :permission="permission"
      />
    </template>

    <div v-if="transaction.multiBill" class="content-section multi-transaction">
      <div class="section-title">{{ $t('payments.intake') }}</div>
      <div class="section-content">
        <span v-if="transaction.billPaymentEnabled">
          {{ $t('common.actived') }}
        </span>
        <span v-else style="color: #FF5172">
          {{ $t('common.stopped') }}
        </span>
        <button v-if="permission" :class="`icon-multi-${[transaction.billPaymentEnabled ? 'off' : 'on']}`" @click="onToggleMultipay"></button>
      </div>
    </div>

    <div v-if="isShownMakerPublishing" class="content-section marketplace-transaction">
      <div class="section-title">
        <a :href="marketplaceUrl" target="_blank" rel="noopener noreferrer">Capusta.Maker</a>
      </div>
      <div class="section-content">
        <span v-if="transaction.onMarketplace" class="marketplace-publish-container" @click="onChangeMarketplaceCategory">
          {{ $t(displayMarketplaceCategory) }}
        </span>
        <span v-else class="marketplace-unpublish-container">
          {{ $t('not.published') }}
        </span>
        <button v-if="permission" :class="`icon-multi-${[transaction.onMarketplace ? 'off' : 'on']}`" @click="onToggleMarketplace"></button>
      </div>
    </div>

    <div v-if="isShownPromoCode" class="content-section promocode-coupon">
      <div class="section-title">{{ $t('promocode.usage') }}</div>
      <div class="section-content">
        <div>{{ applyPromoCode ? $t('common.allowed') : $t('common.denied') }}</div>
        <button v-if="permission" :class="`icon-multi-${[ applyPromoCode ? 'off' : 'on']}`" @click="onTogglePromoCodePermission"></button>
      </div>
    </div>

    <div v-if="transaction.public_id" class="content-section transaction-link">
      <div class="section-title">{{ $t('link.for.pay') }}</div>
      <div class="section-content">
        <a ref="code" :href="transaction.payLink" target="_blank">{{ transaction.payLink }}</a>
        <button v-if="permission" class="icon-copy" @click="onCopy"></button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex';

import { MARKETPLACE_URL } from 'root/.env';

import HTTPService from '@/services/Http';
import logrocket from '@/services/logrocket';

import { EVENT_BUS } from '@/eventBus';

import BillTariff from './components/BillTariff';

import {
  IFRAME_REG_EXP,
  CASUAL,
  SUBSCRIPTION,
  TARIFF,
  TICKET,
  TIPS
} from './const';

import { getAttributeFrom } from './helpers';

const DEFAUL_CATEGORY_CODE = 'OTHERS';

export default {
  name: 'BillCardContent',
  components: {
    BillTariff,
  },
  props: {
    transaction: {
      type: Object,
      required: true,
    },
    permission: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      marketplaceUrl: MARKETPLACE_URL,
    };
  },
  computed: {
    billType() {
      return this.transaction.type;
    },
    billTariffs() {
      return this.transaction.billTariffs;
    },
    isCasualType() {
      return this.billType === CASUAL;
    },
    isSubscription() {
      return this.billType === SUBSCRIPTION;
    },
    isTariff() {
      return this.billType === TARIFF;
    },
    isTicket() {
      return this.billType === TICKET;
    },
    isTips() {
      return this.billType === TIPS;
    },
    isShownDescription() {
      const { description } = this.transaction;

      if (this.$validate.isNotNull(description) && this.$validate.isNotEmptyString(description)) {
        return true;
      }

      if (this.isCasualType) {
        return this.$validate.isNotNull(this.billTariffs[0].contentUrl);
      }

      return false;
    },
    isShownContentUrl() {
      return (this.isCasualType || this.isTips) && this.billTariffs[0].contentUrl;
    },
    getContentUrlByCasualType() {
      if (!this.isShownContentUrl) {
        return null;
      }

      const { contentUrl } = this.billTariffs[0];

      if (this.$validate.isNull(contentUrl) || this.$validate.isEmptyString(contentUrl)) {
        return null;
      }

      return this.getDisplayContentUrl(contentUrl);
    },
    currentCategory() {
      const { marketplaceCategoryId } = this.transaction;

      const currentCategory = this.$validate.isNull(marketplaceCategoryId)
        ? this.MARKETPLACE_CATEGORIES.find(category => category.code === DEFAUL_CATEGORY_CODE)
        : this.MARKETPLACE_CATEGORIES.find(category => Number(category.id) === Number(marketplaceCategoryId));

      return currentCategory;
    },
    displayMarketplaceCategory() {
      return this.$t(this.currentCategory.description);
    },
    splitedDescription() {
      if (!this.transaction.description) {
        return '';
      }

      return this.transaction.description.split('\n');
    },
    isShownPromoCode() {
      if (this.isTips) {
        return false;
      }

      return this.transaction.promoCodeApplicable;
    },
    applyPromoCode() {
      return this.transaction.applyPromoCode;
    },
    tipsVariants() {
      return this.transaction?.tipsVariants || null;
    },
    isShownMakerPublishing() {
      if (this.isTips) {
        return false;
      }

      return this.makerPublishingPermission;
    },
    ...mapGetters(['MARKETPLACE_CATEGORIES', 'makerPublishingPermission']),
  },
  methods: {
    getDisplayContentUrl(contentUrl) {
      if (IFRAME_REG_EXP.test(contentUrl)) {
        return getAttributeFrom(contentUrl, 'src');
      }

      return contentUrl;
    },
    async onToggleMultipay() {
      const publicId = this.transaction.public_id;

      const requestData = {
        billPaymentEnabled: !this.transaction.billPaymentEnabled,
      };

      try {
        await HTTPService.editBill(requestData, publicId);

        // eslint-disable-next-line vue/no-mutating-props
        this.transaction.billPaymentEnabled = !this.transaction.billPaymentEnabled;

        this.$alert({
          type: 'success',
          text: this.transaction.billPaymentEnabled ? 'bill.activated' : 'bill.stopped',
        });
      } catch (error) {
        logrocket.logger(error);

        const { data } = error;

        if (data.status === 'CURRENCY_MISMATCH') {
          this.$alert({
            type: 'warning',
            text: 'take.new.amount',
          });
          return;
        }

        if (data.status === 'NO_TICKETS_LEFT') {
          this.$alert({
            type: 'warning',
            text: 'bill.ticket.error.no.tickets.left',
          });
          return;
        }

        this.$alert({
          type: 'warning',
          text: 'problems.with.server',
        });
      }
    },
    onToggleMarketplace() {
      if (!this.makerPublishingPermission) {
        return;
      }

      HTTPService.editBill(
        {
          onMarketplace: !this.transaction.onMarketplace,
          marketplaceCategoryId: this.currentCategory.id,
        },
        this.transaction.public_id
      )
        .then(() => {
          this.CHANGE_SELECTED_TRANSACTION_MARKETPLACE(!this.transaction.onMarketplace);
        })
        .catch(error => {
          logrocket.logger(error);
        });
    },
    onChangeMarketplaceCategory() {
      if (!this.makerPublishingPermission || !this.permission) {
        return;
      }

      this.SET_MODAL_PARAMS({
        type: 'MARKETPLACE_CATEGORIES',
        params: {
          show: true,
          billCode: this.transaction.public_id,
          categoryCode: this.currentCategory.code,
          categories: this.MARKETPLACE_CATEGORIES,
        },
      });
    },
    onCopy() {
      this.$copyText(this.transaction.payLink);

      this.$alert({
        type: 'ok',
        text: 'link.copied',
      });
    },
    async onTogglePromoCodePermission() {
      try {
        const { data } = await HTTPService.editBill(
          { applyPromoCode: !this.applyPromoCode },
          this.transaction.public_id
        );

        this.$alert({
          type: 'ok',
          text: data.applyPromoCode ? 'promocode.usage.is.allowed' : 'promocode.usage.is.denied',
        });

        this.CHANGE_SELECTED_TRANSACTION_APPLY_PROMO_CODE(data.applyPromoCode);
      } catch (error) {
        logrocket.logger(error);

        this.$alert({
          type: 'warning',
          text: 'promocode.usage.is.impossible',
        });
      }
    },
    handleChangeTipsVariants() {
      if (this.permission) {
        EVENT_BUS.$emit('EVENT_SHOW_TIPS_VARIANTS_EDITING', this.transaction.tipsVariants);
      }
    },
    ...mapMutations([
      'SET_MODAL_PARAMS',
      'CHANGE_SELECTED_TRANSACTION_MARKETPLACE',
      'CHANGE_SELECTED_TRANSACTION_APPLY_PROMO_CODE',
    ]),
  },
};
</script>

<style lang="sass" module>
  .tipsSection
    +inline-flex
    +font-type('medium', 12, 20)
    color: var(--color-black)
    background-color: #E9E9E9
    border-radius: 20px
    padding: 3px 5px

  .tipsVariant
    +inline-flex
    position: relative
    padding: 0 10px

    &:not(:last-child)
      &:after
        content: ''
        width: 1px
        height: 10px
        position: absolute
        right: 0
        top: 50%
        transform: translateY(-50%)
        background-color: #000
        opacity: .1
</style>

<style lang="sass" scoped>
  @mixin square($val)
    width: $val
    height: $val

  %icon-bg
    background:
      color: transparent
      repeat: no-repeat
      position: center

  %button-icon
    position: absolute
    +square(2rem)
    @extend %icon-bg

  .content-container
    padding: 0 30px

    +adaptive(mobile)
      padding: 0 14px

  .content-section
    position: relative
    padding: 26px 2rem 1.4rem 3.6rem

    +adaptive(mobile)
      padding: 1.8rem 2rem 1.8rem 3rem

    &::before
      @extend %button-icon
      content: ''
      left: 0
      top: 20px
      +adaptive(mobile)
        top: 1.3rem

  .section-title
    margin-bottom: 10px
    font-weight: 500
    font-size: 12px
    line-height: 18px
    color: #212121
    opacity: .5

    a
      color: #212121

  .section-content
    word-break: break-word
    font-weight: 500
    font-size: 1rem
    position: relative

    a
      font-weight: 500
      font-size: 14px
      line-height: 20px
      color: #000
      text-decoration: underline

      &:hover
        text-decoration: none

    span
      font-weight: 500
      font-size: 14px
      line-height: 20px
      color: #000

    .button > span
      font-size: 1rem

    p
      font-weight: 500
      font-size: 12px
      line-height: 18px
      color: #212121

    button
      @extend %button-icon
      top: 50%
      right: -2rem
      transform: translateY(-50%)

    .section-url
      font-weight: 500
      font-size: 14px
      line-height: 20px
      color: #212121
      vertical-align: middle

      &::before
        content: ''
        background: get-icon(clip, #333) no-repeat
        background-size: contain
        display: inline-block
        vertical-align: middle
        width: 20px
        height: 15px

    .section-description + .section-url
      margin-top: 8px

    .marketplace-publish-container
      font-size: 1rem
      font-weight: 500
      color: #9B51E0
      cursor: pointer

    .marketplace-unpublish-container
      font-size: 1rem
      font-weight: 500
      color: #212121

  .icon-multi-on
    background-image: get-icon(multi-on, #9B51E0, $transactionHeaderIcons)

  .icon-multi-off
    background-image: get-icon(multi-off, #9B51E0, $transactionHeaderIcons)

  .icon-receipt
    background-image: get-icon(receipt, #9B51E0, $misc)

  .icon-copy
    background-image: get-icon(copy, #9B51E0, $transactionFooterIcons)

  @mixin contentIcons($icon, $color: #212121)
    &::before
      background-image: get-icon($icon, $color, $transactionsContentIcons)

  // ICONS
  .comission
    +contentIcons(percent)

  .description
    +contentIcons(description)

  .tips
    border-top: 0.08rem solid #E3E3E4
    +contentIcons(tips)

  .multi-transaction
    border-top: 0.08rem solid #E3E3E4
    +contentIcons(payment)

    .multi-off &
      &::before
        opacity: .5

  .marketplace-transaction
    border-top: 0.08rem solid #E3E3E4
    +contentIcons(planet)

    .marketplace-off &
      &::before
        opacity: .5

  .promocode-coupon
    border-top: 0.08rem solid #E3E3E4
    +contentIcons(coupon)

  .transaction-link
    border-top: 0.08rem solid #E3E3E4
    +contentIcons(link)
</style>
